import { formattaDataOra } from './formatoData';

// Vocabulary of the iot-temperature domain (CONTEXT.md, ADR-0008):
// - Rimappatura = the immutable trace the publication writes for every
//   Assegnazione it moved or closed. A `RIMAPPATA` voce followed its subject
//   into the new manual; a `CHIUSA` one did not find the subject any more.
// - Coda di revisione = only the CHIUSA voci nobody has reviewed yet. A CHIUSA
//   voce is a fridge that stopped being watched, and the absence of alarms is
//   indistinguishable from normal operation (US 33): that is why the queue has
//   to make itself noticed, not merely exist.
// - Revisione = the answer to a voce, which closes it: reassignment of the probe
//   to a Controllo of the new manual, or archiving with a written reason. It
//   lives on a row of its own, written once: the trace is never touched.

/** When the trace carries no attrezzatura of provenance. */
export const ATTREZZATURA_NON_REGISTRATA = 'Non registrata';

/** When the trace carries no Controllo of provenance. */
export const CONTROLLO_NON_REGISTRATO = 'Non registrato';

// What the cell says when the trace's old id has no name to show. It names
// *where* the subject was — the retired revision of the manual — and not the
// outcome of a lookup: «id non risolvibile» is a sentence about this client's
// data, and there is nothing an operator can do with it. Which subject actually
// went missing is said by the trace sentence next to it, which is the only
// authority on it.
export const ATTREZZATURA_DEL_MANUALE_PRECEDENTE =
  'Attrezzatura del manuale precedente';
export const CONTROLLO_DEL_MANUALE_PRECEDENTE =
  'Controllo del manuale precedente';

/** When the archiving revision carries no reason. */
export const RAGIONE_NON_REGISTRATA = 'Non registrata';

// One text per column, each saying *what* is missing. They stay separate because
// a shared literal reads as nonsense once two of them meet in one sentence - the
// archive modal composes «<ragione> del <istante>» - and because a constant named
// after the attrezzatura would carry a retext of that placeholder into the
// revision cell.
const REVISIONE_NON_REGISTRATA = 'revisione non registrata';
const ISTANTE_NON_REGISTRATO = 'istante non registrato';

/**
 * Raised when the queue answered something this client cannot read as a list.
 *
 * It is an error and not an empty list on purpose: `wrapFetchWithDetailedErrors`
 * resolves the raw `Response` for any 2xx that is not `application/json` (a
 * gateway or an auth redirect answering `200 text/html`), and rendering that as
 * «no Assegnazione to review» would assert that no fridge was left unwatched —
 * the exact silent loss US 33 exists to close.
 */
export class ErroreRispostaCoda extends Error {
  constructor() {
    super('La risposta del servizio non è in un formato leggibile.');
    this.name = 'ErroreRispostaCoda';
  }
}

// The route answers a bare array; the keys are kept for the only pagination the
// panel could receive without changing screen. Anything else is unreadable, not
// empty.
const estraiVoci = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;

  const chiave = ['voci', 'content', 'items'].find((nome) =>
    Array.isArray(payload[nome]),
  );
  return chiave ? payload[chiave] : null;
};

/**
 * The shape on the wire is `VoceRimappaturaChiusaDTO`: flat fields.
 *
 * `attrezzaturaId` and `controlloId` are the trace's **old** ids, not those of
 * the published manual: the publication that closed the voce is the same one
 * that retired them, so nothing more recent exists that says where the probe
 * was. `descrizione` is on the trace for exactly this case.
 *
 * Throws `ErroreRispostaCoda` when the payload is not a recognised shape.
 */
export const normalizzaVociRimappatura = (payload) => {
  const voci = estraiVoci(payload);
  if (voci === null) throw new ErroreRispostaCoda();

  return voci.map((voce) => ({
    id: voce.id,
    assegnazioneId: voce.assegnazioneId ?? null,
    sn: voce.sn ?? '',
    attrezzaturaId: voce.attrezzaturaId ?? null,
    controlloId: voce.controlloId ?? null,
    pubblicazioneRevisione: voce.pubblicazioneRevisione ?? null,
    creatoIl: voce.creatoIl ?? null,
    descrizione: voce.descrizione ?? '',
  }));
};

/**
 * The count of open voci, or `null` when the response does not carry one.
 *
 * `null` and not `0`: «no voce to review» is an assertion, and making it on a
 * response that was not understood would put back the silent loss this queue
 * exists to close.
 */
export const leggiConteggioVociAperte = (payload) =>
  typeof payload?.aperte === 'number' && Number.isFinite(payload.aperte)
    ? payload.aperte
    : null;

/** The «rev. N» of ADR-0008: the ordinal of the publication that closed the voce. */
export const descriviRevisionePubblicazione = (revisione) =>
  revisione == null ? REVISIONE_NON_REGISTRATA : `rev. ${revisione}`;

/**
 * The instant the publication closed the voce, at the reader's timezone — as
 * `dettaglioStatoSonda` does for the last reading of the sonda.
 */
export const descriviIstante = (istante) =>
  formattaDataOra(istante) ?? ISTANTE_NON_REGISTRATO;

/**
 * The attrezzatura of provenance.
 *
 * The name is looked up in the published manual, but a miss does **not** mean
 * the attrezzatura is gone: ADR-0008 says `ResetIdsStep` renumbers to a fresh id
 * every entity the publication touched, and the reset cascades — an attrezzatura
 * that is alive and well takes a new id merely because its `Locale` was edited.
 * Reading that new id as «no longer in the manual» is the very «un id nuovo letto
 * come soggetto assente» mistake that ADR is written to stop, and it lands on the
 * screen where an operator chooses between reassigning and archiving. So on a
 * miss the cell names where the attrezzatura was, without asserting it is gone —
 * and without the old id, which `descriviIdProvenienza` keeps for support. The
 * trace sentence next to it stays the authority on what actually went missing.
 */
export const descriviProvenienzaAttrezzatura = (voce, attrezzature = []) => {
  if (voce?.attrezzaturaId == null) return ATTREZZATURA_NON_REGISTRATA;

  const viva = (attrezzature ?? []).find(
    (attrezzatura) => String(attrezzatura.id) === String(voce.attrezzaturaId),
  );

  return viva?.nome ?? ATTREZZATURA_DEL_MANUALE_PRECEDENTE;
};

/**
 * The Controllo of provenance. No list to query: the Controllo name is assembled
 * by the server (`NomeControllo`) and the queue route sends only the id — so
 * there is no name to show and, unlike the attrezzatura, nothing was looked up.
 * The cell therefore says only what is certain, that the Controllo belonged to
 * the manual the publication retired; the id goes to `descriviIdProvenienza`,
 * and the trace sentence in the row stays the authority on what went missing.
 *
 * «Id non risolvibile» is not the fallback here, unlike for the attrezzatura:
 * nothing is looked up on this axis, so that text would be stamped on every row -
 * including the ones closed because the *attrezzatura* disappeared while the
 * Controllo is still there.
 */
export const descriviProvenienzaControllo = (voce) =>
  voce?.controlloId == null
    ? CONTROLLO_NON_REGISTRATO
    : CONTROLLO_DEL_MANUALE_PRECEDENTE;

/**
 * The trace's old ids, for support only — a `title`, never the cell text.
 *
 * They are renumbered by the publication (ADR-0008), they appear on no other
 * screen, they are not searchable and they belong to a revision of the manual
 * that has been retired: nothing an operator can act on. `null` when the trace
 * carries no id at all, so the caller has no empty tooltip to render.
 */
export const descriviIdProvenienza = (voce) => {
  const parti = [
    voce?.attrezzaturaId == null ? null : `attrezzatura ${voce.attrezzaturaId}`,
    voce?.controlloId == null ? null : `controllo ${voce.controlloId}`,
  ].filter(Boolean);

  return parti.length ? `Id nel manuale ritirato: ${parti.join(', ')}.` : null;
};

/**
 * The publication that opened the voce, as one clause. Both halves can be
 * missing, and each says which one it is instead of borrowing the other's text.
 */
export const descriviPubblicazioneCheHaChiuso = (voce) =>
  `${descriviRevisionePubblicazione(voce?.pubblicazioneRevisione)}, ` +
  `${descriviIstante(voce?.creatoIl)}`;

/** The voci with the labels the row shows, computed once. */
export const vociConProvenienza = ({ voci = [], attrezzature = [] }) =>
  voci.map((voce) => ({
    ...voce,
    attrezzaturaEtichetta: descriviProvenienzaAttrezzatura(voce, attrezzature),
    controlloEtichetta: descriviProvenienzaControllo(voce),
    idProvenienzaEtichetta: descriviIdProvenienza(voce),
    revisioneEtichetta: descriviRevisionePubblicazione(
      voce.pubblicazioneRevisione,
    ),
    istanteEtichetta: descriviIstante(voce.creatoIl),
  }));

/** The archiving response, in the shape of `RevisioneRimappaturaDTO`. */
export const normalizzaRevisione = (payload) =>
  payload
    ? {
        rimappaturaId: payload.rimappaturaId ?? null,
        azione: payload.azione ?? null,
        ragione: payload.ragione ?? null,
        nuovaAssegnazioneId: payload.nuovaAssegnazioneId ?? null,
        revisionataDa: payload.revisionataDa ?? null,
        revisionataIl: payload.revisionataIl ?? null,
      }
    : null;

/** The three outcomes of the pre-write guard below. */
export const ESITO_VOCE_INVARIATA = 'invariata';
export const ESITO_VOCE_REVISIONATA = 'revisionata';
export const ESITO_VOCE_NON_VERIFICABILE = 'nonVerificabile';

/**
 * Conditional-write guard on the queue. The queue has a second writer in
 * production: another back office workstation archiving the same voce, and
 * bs-safe-app reassigning the probe from the shop floor — creating an
 * Assegnazione on that probe closes its voci in the same transaction as the new
 * row (`IotAssegnazioneCrudService.crea`). The routes carry no version and no
 * ETag, so the queue is re-read right before archiving and the write is
 * abandoned when the voce is no longer open.
 *
 * The uniqueness constraint on `rimappatura_id` holds the race server-side
 * anyway: this guard is here so the wrong message cannot win — «archived» on a
 * voce somebody else closed by reassigning the probe is the opposite outcome.
 *
 * Three outcomes and not two: «the list could not be read» is not «somebody else
 * already reviewed it», and telling the operator the second on the first is a
 * false statement about a write that never happened.
 */
export const esitoConfrontoVoce = (voce, elencoAggiornato) => {
  if (!Array.isArray(elencoAggiornato)) {
    return {
      esito: ESITO_VOCE_NON_VERIFICABILE,
      invariata: false,
      messaggio:
        'Non è stato possibile verificare lo stato di questa voce: riprova.',
    };
  }

  const ancoraAperta = elencoAggiornato.some(
    (riga) => String(riga.id) === String(voce?.id),
  );

  return ancoraAperta
    ? { esito: ESITO_VOCE_INVARIATA, invariata: true, messaggio: null }
    : {
        esito: ESITO_VOCE_REVISIONATA,
        invariata: false,
        messaggio:
          'Questa voce è già stata revisionata da un’altra postazione: la sonda' +
          ' è stata riassegnata o la voce archiviata. Coda aggiornata.',
      };
};
